import { writable } from "svelte/store";

import type { Colour, Tile, TileType } from "../types/tile";
import type Server from "../misc/server";
import { Message } from "../misc/message";
import WebsocketServer from "../misc/server/websocketServer";

type BoardTile = {
    Colour: Colour;
    TileType: TileType;
    X: number;
    Y: number;
};

function createWebsocket() {
    const { subscribe, set, update } = writable<Server>(null);

    let server: Server;
    let subscriptionRemovers: Function[] = [];

    const unsubscribeFromAll = () => {
        for (const subscriptionRemover of subscriptionRemovers) {
            subscriptionRemover();
        }
        subscriptionRemovers = [];
    };

    return {
        subscribe,
        connect: async (serverName: string): Promise<boolean> => {
            try {
                const newServer = new WebsocketServer(serverName);
                await newServer.createConnection();

                set(newServer);
                server = newServer;

                return true;
            } catch (e) {
                console.error(e);
                return false;
            }
        },
        start: async (name: string): Promise<string[]> => {
            // Sends 'start' message
            const args = { Name: name };
            const msg = new Message("start", args);
            server.sendMessage(msg);

            // Await next message
            const nextMessage = await server.awaitMessage();

            if (nextMessage.Command === "welcome")
                return nextMessage.Parameters.Names;
            else if (nextMessage.Command === "no_welcome")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        iAmReady: (): void => {
            const msg = new Message("i_am_ready", {});
            server.sendMessage(msg);
        },
        sendChatMessage: (message: string) => {
            const args = { Message: message };
            server.sendMessage(new Message("send_message", args));
        },
        onReceiveChatMessage: (
            handler: (senderName: string, chatMessage: string) => void
        ) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("receive_message", (parameters) =>
                    handler(parameters.Name, parameters.Message)
                ),
            ];
        },
        onNewPlayer: (handler: (newPlayerName: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("new_player", (parameters) => {
                    handler(parameters.Name);
                }),
            ];
        },
        onLostPlayer: (handler: (lostPlayer: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("lost_player", (parameters) => {
                    handler(parameters.Name);
                }),
            ];
        },
        onTheyAreReady: (handler: (readyPlayer: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("they_are_ready", (parameters) => {
                    handler(parameters.Name);
                }),
            ];
        },
        mayIAddTile: async (
            tile: Tile,
            x: number,
            y: number
        ): Promise<void> => {
            // Asks server to put new tile here
            const params = {
                ...tile,
                X: x,
                Y: y,
            };
            const msg = new Message("may_i_add_tile", params);
            server.sendMessage(msg);

            // Gets response of whether we can
            const nextMessage = await server.awaitMessage();

            if (nextMessage.Command === "you_may_add_tile") return;
            else if (nextMessage.Command === "you_may_not_add_tile")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        onYouMayAddTile: (handler: () => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("you_may_add_tile", (_) => {
                    handler();
                }),
            ];
        },
        onTheyAddedTile: (
            handler: (tile: Tile, x: number, y: number) => void
        ) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("they_added_tile", (parameters) => {
                    handler(
                        {
                            Colour: parameters.Colour,
                            TileType: parameters.TileType,
                        },
                        parameters.X,
                        parameters.Y
                    );
                }),
            ];
        },
        onYouMayNotAddTile: (handler: (reason: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage(
                    "you_may_not_add_tile",
                    (parameters) => {
                        handler(parameters.Reason);
                    }
                ),
            ];
        },
        mayIMoveTile: async (
            x: number,
            y: number,
            deltaX: number,
            deltaY: number
        ): Promise<void> => {
            // Asks server if we can move a tile
            const params = {
                X: x,
                Y: y,
                DeltaX: deltaX,
                DeltaY: deltaY,
            };
            const msg = new Message("may_i_move_tile", params);
            server.sendMessage(msg);

            // Gets response of whether we can
            const nextMessage = await server.awaitMessage();

            if (nextMessage.Command === "you_may_move_tile") return;
            else if (nextMessage.Command === "you_may_not_move_tile")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        onYouMayMoveTile: (handler: () => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("you_may_move_tile", (_) => {
                    handler();
                }),
            ];
        },
        onTheyMovedTile: (
            handler: (
                x: number,
                y: number,
                deltaX: number,
                deltaY: number
            ) => void
        ) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("they_moved_tile", (parameters) => {
                    handler(
                        parameters.X,
                        parameters.Y,
                        parameters.DeltaX,
                        parameters.DeltaY
                    );
                }),
            ];
        },
        onYouMayNotMoveTile: (handler: (reason: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage(
                    "you_may_not_move_tile",
                    (parameters) => {
                        handler(parameters.Reason);
                    }
                ),
            ];
        },
        mayIRemoveTile: async (x: number, y: number): Promise<void> => {
            // Asks server if we can remove tile
            const params = { X: x, Y: y };
            const msg = new Message("may_i_remove_tile", params);
            server.sendMessage(msg);

            // Gets response of whether we can
            const nextMessage = await server.awaitMessage();

            if (nextMessage.Command === "you_may_remove_tile") return;
            else if (nextMessage.Command === "you_may_not_remove_tile")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        onTheyRemovedTile: (handler: (x: number, y: number) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("they_removed_tile", (parameters) => {
                    handler(parameters.X, parameters.Y);
                }),
            ];
        },
        mayIEndTurn: async (): Promise<Tile | null> => {
            // Asks server if we can end turn
            const msg = new Message("may_i_end_turn", {});
            server.sendMessage(msg);

            // Gets response of whether we can
            const nextMessage = await server.awaitMessage();

            if (nextMessage.Command === "you_may_end_turn")
                return nextMessage.Parameters.Tile;
            else if (nextMessage.Command === "you_may_not_end_turn")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        onNextPlayer: (handler: (currentPlayer: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("next_player", (parameters) => {
                    handler(parameters.CurrentPlayer);
                }),
            ];
        },
        mayIResetBoard: async (): Promise<void> => {
            // Asks server if we can end turn
            const msg = new Message("may_i_reset_board", {});
            server.sendMessage(msg);

            // Gets response of whether we can
            const nextMessage = await server.awaitMessage();

            if (
                nextMessage.Command === "reset_board" ||
                nextMessage.Command === "update_tray"
            ) {
                return;
            } else if (nextMessage.Command === "you_may_not_reset_board")
                throw nextMessage.Parameters.Reason;
            else throw "Unknown response";
        },
        onResetBoard: (handler: (boardTiles: BoardTile[]) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("reset_board", (parameters) => {
                    handler(parameters.BoardTiles);
                }),
            ];
        },
        onUpdateTray: (handler: (trayTiles: Tile[]) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("update_tray", (parameters) => {
                    handler(parameters.TrayTiles);
                }),
            ];
        },
        onSetTileCount: (
            handler: (player: string, tileCount: number) => void
        ) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("set_tile_count", (parameters) => {
                    handler(parameters.Player, parameters.TileCount);
                }),
            ];
        },
        onReadyInterrupt: (handler: () => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("ready_interrupt", () => {
                    handler();
                }),
            ];
        },
        onGameStart: (
            handler: (
                tiles: Tile[],
                firstPlayer: string,
                initialMeld: boolean
            ) => void
        ) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("game_start", (parameters) => {
                    const tiles = parameters.Tiles;
                    const firstPlayer = parameters.FirstPlayer;
                    const initialMeld = parameters.InitialMeld;
                    handler(tiles, firstPlayer, initialMeld);
                }),
            ];
        },
        onGameFinish: (handler: (winner: string) => void) => {
            subscriptionRemovers = [
                ...subscriptionRemovers,
                server.subscribeToMessage("game_finish", (parameters) => {
                    const winner = parameters.Winner;
                    handler(winner);
                }),
            ];
        },
        unsubscribeFromAll,
        close: () => {
            unsubscribeFromAll();
            server.closeConnection();
        },
    };
}

export const websocket = createWebsocket();
